import { useQuery } from "react-query";
import React, { lazy, memo, Suspense, useEffect, useState } from "react";
import { fetchTagPolymer } from "@Services/api";
import { fetchTagKindss, fetchV3Articles, fetchRecommendHot } from "@Services/article";
import { Pagination, Button, Skeleton } from "antd";
import RelatedCar from "./RelatedCar";
import withLayout from "../../../../src/layouts/withLayout";
import TagsColumn from "@Components/tags/TagsColumn";

const RelatedModels = lazy(() => import("@Components/tags/RelatedModels"));
const ArticlesRank = lazy(() => import("@Components/tags/ArticlesRank"));


const TagDescription: React.FC<{ description: string }> = memo(({ description }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!!description) {
      setIsLoaded(true);
    }
  }, [description]);

  return (
    <>
      {!isLoaded ? (
        <Skeleton active />
      ) : (
        <p className="text-20 text-[#666666] leading-[24px] mb-0" dangerouslySetInnerHTML={{ __html: description }}></p>
      )}
    </>
  );
});


const TagsPage: React.FC<{}> = () => {
  const page: number = window.page || 1;
  const tag: string = window.tag || "";
  const tagTitle: string = window.tagTitle || "";
  const [total, setTotal] = useState<number>(10);
  const [titleLeft, setTitleLeft] = useState<boolean>(false);

  useEffect(() => {
    const title = document.getElementById("tag-title");

    if (title?.clientHeight && title?.clientHeight > 36) {
      setTitleLeft(true);
    }
  }, [tagTitle]);

  const { data = {} } = useQuery("fetchTagPolymer", async () => {
    const res = await fetchTagPolymer({
      tag,
    });

    if (res?.status === 12000) {
      return res.data;
    }
  });

  const { data: articleData } = useQuery(`fetchV3Articles-${page}`, async () => {
    const res = await fetchV3Articles({
      tag,
      page,
      limit: 20,
      contentDescription: 1,
      type: "1,2",
      appendSimilarTag: 1,
    });

    if (res?.status === 12000) {
      setTotal(res.data.total);
      return res.data;
    }
  });

  const { data: relatedCar } = useQuery(`fetchTagKindss`, async () => {
    const res: any = await fetchTagKindss({
      type: "article",
      tagName: tag,
      page: 1,
      limit: 9,
    });

    if (res?.status === 12000) {
      return res.data.list;
    }
  });

  const itemRender = (current, type, originalElement) => {
    if (type === "prev") {
      return (
        <Button>
          <a href={`/article/tag/${tag}?page=${page - 1}`}>&lt;上一頁</a>
        </Button>
      );
    }

    if (type === "next") {
      return (
        <Button>
          <a href={`/article/tag/${tag}?page=${page + 1}`}>下一頁&gt;</a>
        </Button>
      );
    }

    if (type === "page") {
      return <a href={`/article/tag/${tag}?page=${current}`}>{current}</a>;
    }

    return originalElement;
  };

  return (
    <div className="w-[1190px] mx-auto mb-40 min-h-screen">
      <div className="flex pt-40">
        <div className="flex-1 border-0 border-r-2 border-[#e8e8e8] border-solid mr-30 pr-40">
          <h1
            className={`${titleLeft ? "text-left" : "text-center"} min-h-[36px] text-30 font-[700] leading-[36px] mb-26`}
            id='tag-title'
          >
            <span className="text-[#3264ff]">#</span>
            {tagTitle}
            <span className="text-[#3264ff]">#</span>
          </h1>

          {!!window.tagDescription && (
            <TagDescription description={window.tagDescription} />
          )}

          <RelatedCar
            style={{ width: "748px", minHeight: "360px" }}
            source={"標籤詳情頁-相關車款"}
            gaId={"Tag"}
            scrollNumber={[748, -732, 732]}
            dataSource={relatedCar}
          />

          <div className="flex flex-col">
            {Array.isArray(articleData?.list) && !!articleData.list.length && (
              articleData.list.map((item) => (
                <a href={item.link} className="py-20 flex" key={item.link}>
                  <div className="w-[260px] h-[172px] mr-16">
                    <picture>
                      <source srcSet={item.thumb} type="image/jpeg" />
                      <img
                        src={item.thumb}
                        alt={item.title}
                        width={260}
                        height={172}
                        loading="eager"
                        {...({ fetchPriority: "high" } as any)}
                      />
                    </picture>
                  </div>

                  <div className="flex flex-1 flex-col justify-between">
                    <div className="text-30 font-[700] leading-[36px] mb-26">
                      <p className="h-48 text-20 font-[500] mb-4 text-left text-[#333333] leading-24 line-clamp-2">{item.title}</p>
                      <div className="text-14 text-[#666666] font-normal text-left leading-[24px] line-clamp-2" dangerouslySetInnerHTML={{ __html: item.contentDescription }}></div>
                    </div>

                    <div className="h-16 text-14 flex text-[#999] gap-x-20">
                      <span>{item.tags[0]}</span>
                      <span>{item.time}</span>
                      <span>{item.fullVisits}</span>
                    </div>
                  </div>
                </a>
              ))
            )}
          </div>

          <div className="flex justify-center">
            <Pagination
              defaultCurrent={1}
              total={total}
              itemRender={itemRender}
              pageSize={20}
              showSizeChanger={false}
              current={page}
              hideOnSinglePage
            />
          </div>
        </div>
        <div className="w-[370px]">
          {/* 熱門標簽 */}
          {Array.isArray(data.hotTag) && !!data.hotTag.length && (
            <TagsColumn data={data.hotTag} title='熱門標簽' />
          )}

          {/* 相關標簽 */}
          {Array.isArray(data.recommendTag) && !!data.recommendTag.length && (
            <TagsColumn data={data.recommendTag} title='相關標簽' />
          )}

          {/* 相關車款 */}
          {Array.isArray(data.kind) && !!data.kind.length && (
            <Suspense fallback={<Skeleton />}>
              <RelatedModels data={data.kind} />
            </Suspense>
          )}

          {/* 熱門文章 */}
          <Suspense fallback={<Skeleton />}>
            <ArticlesRank
              title={
                <h2 className="text-28 font-[700] text-[#333333] leading-[30px]">
                  <i className="text-[#ff0000] italic mr-6">熱門</i>文章
                </h2>
              }
              fetchData={{ limit: 10 }}
              fetchFun={fetchRecommendHot}
              fetchKey='fetchRecommendHot'
            />
          </Suspense>

          {/* 最新文章 */}
          <Suspense fallback={<Skeleton />}>
            <ArticlesRank
              title={<h2 className="text-28 font-[700] text-[#333333] leading-[30px]">最新文章</h2>}
              fetchData={{ limit: 10 }}
              fetchFun={fetchV3Articles}
              fetchKey='fetchV3Articles'
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

withLayout({
  cmp: <TagsPage />,
  params: {
    needRTBH: true,
    AdTopBanner: true,
    nav: "DefaultFixedNav",
    footer: "Footer",
  },
});